import { getKeyValue } from "@nextui-org/react";
import { Key, useCallback, useState } from "react";
import { usePaginateTransactions } from "@/services/admin.service";
import { format } from "date-fns";
import { TransactionTypeEnum } from "@/components/admin/transactionmodule/types.tsx";
import Datatable from "@/components/tables/datatable";
import {
  DateValue,
  getLocalTimeZone,
  parseDate,
} from "@internationalized/date";
import CustomDropdown from "../customdropdown";
import { TransactionStatus } from "@/components/general/TransactionStatus";
import { TransactionDetailDialog } from "./transactionDetailModal";
import { DateRangePicker } from "@/components/ui/daterangepicker";

const fields = [
  { label: "Username", value: "username" },
  { label: "Trx.ID", value: "id" },
];

const paymentMethods = [
  {
    label: "All",
    value: "",
  },
  {
    label: "Crypto",
    value: "Crypto",
  },
  {
    label: "Bank Transfer",
    value: "BANK_TRANSFER",
  },
  {
    label: "Card Payment",
    value: "CARD_PAYMENT",
  },
];

const paymentStatus = [
  {
    label: "All",
    value: "",
  },
  {
    label: "Failed",
    value: "FAILED",
  },
  {
    label: "Completed",
    value: "COMPLETED",
  },
];

const columns = [
  {
    key: "no",
    label: "No",
  },
  {
    key: "trxid",
    label: "Trx.ID",
  },
  {
    key: "date",
    label: "Date",
  },
  {
    key: "username",
    label: "Username",
  },
  {
    key: "method",
    label: "Method",
  },
  {
    key: "amount",
    label: "Amount",
  },
  {
    key: "currency",
    label: "Type",
  },
  {
    key: "status",
    label: "Status",
  },
  {
    key: "action",
    label: "Action",
  },
];

type transactionFilter = {
  transactionType: TransactionTypeEnum;
  method: string;
  status: string;
  startDate: string;
  endDate: string;
  username: string;
};

const TransactionList = ({
  transactionType,
}: {
  transactionType: TransactionTypeEnum;
}) => {
  const [filter, setFilter] = useState<transactionFilter>({
    transactionType: transactionType,
    method: "",
    status: "",
    startDate: format(new Date(), "yyyy-MM-dd"),
    endDate: format(new Date(), "yyyy-MM-dd"),
    username: "",
  });
  const [selectedTransactionId, setSelectedTransactionId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);

  const query = usePaginateTransactions;

  const renderCell = useCallback((data: any[], item: any, columnKey: Key) => {
    switch (columnKey) {
      case "no":
        return (
          <div className="">
            {" "}
            {data.findIndex((row) => (row as any)?.id === (item as any)?.id) +
              1}
          </div>
        );
      case "trxid":
        return <div className="">{item?.transactionId}</div>;
      case "date":
        return (
          <div className="font-bold">
            {item.createdAt
              ? format(new Date(item.createdAt), "dd/MM/yyyy hh:mm:ss")
              : ""}
          </div>
        );
      case "from":
        return <div className="">{item?.walletAddress}</div>;
      case "amount":
        return <div className="">{item?.amount}</div>;
      case "username":
        return <div className="">{item?.User.userName}</div>;
      case "method":
        return <div className="">{item?.method}</div>;
      case "status":
        return TransactionStatus(
          item.isCompleted,
          item.isRejected,
          item.isCanceled,
        );
      case "action":
        return !item?.isCompleted ? (
          <div
            onClick={() => {
              setSelectedTransactionId(item?.id);
              setIsModalOpen(true);
            }}
            className="w-[5rem] rounded-lg border-1 border-primary-text p-1 text-center text-primary-text hover:cursor-pointer"
          >
            Review
          </div>
        ) : (
          <div
            onClick={() => {
              setSelectedTransactionId(item?.id);
              setIsModalOpen(true);
            }}
            className="text-secondary hover:cursor-pointer"
          >
            See Details
          </div>
        );
      default:
        return (
          <div className="font-normal">
            {getKeyValue(item, columnKey as string) || "-"}
          </div>
        );
    }
  }, []);

  return (
    <div>
      <div className="font-ubuntu float-left text-center text-sm font-semibold text-white sm:text-lg">
        <p className="hidden sm:block">{`${transactionType
          .toLowerCase()
          .split("")
          .map((s: string, i: number) => (i === 0 ? s.toUpperCase() : s))
          .join("")} Transaction List`}</p>
      </div>

      <Datatable
        columns={columns}
        renderCell={renderCell}
        query={query}
        triggerRefetch={refetch}
        onRefetch={() => setRefetch(false)}
        customQueryFilter={{
          ...filter,
        }}
        customFilters={[
          <div className="min-w-[150px] sm:w-1/6">
            <CustomDropdown
              placeholder="Select Method"
              value={filter.method || ""}
              onSelect={(e) => {
                const newFilter = { ...filter, method: e?.value || "" };
                setFilter(newFilter);
              }}
              options={paymentMethods}
            />
          </div>,

          <div className="min-w-[150px] sm:w-1/6">
            <CustomDropdown
              placeholder="Select Status"
              value={filter.status || ""}
              onSelect={(e) => {
                const newFilter = { ...filter, status: e?.value || "" };
                setFilter(newFilter);
              }}
              options={paymentStatus}
            />
          </div>,

          <DateRangePicker
            onChange={(e: any) => {
              const startDate = format(
                e.start.toDate(getLocalTimeZone()),
                "yyyy-MM-dd",
              );
              const endDate = format(
                e.end.toDate(getLocalTimeZone()),
                "yyyy-MM-dd",
              );
              const newFilter = { ...filter, startDate, endDate };
              setFilter(newFilter);
            }}
          />,
        ]}
        searchFields={fields}
      />

      <TransactionDetailDialog
        transactionId={selectedTransactionId}
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onRefetch={() => setRefetch(true)}
      />
    </div>
  );
};

export default TransactionList;
